import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
  listSupportTickets,
  getSupportTicketMetrics,
  assignSupportTicket,
  updateSupportTicketStatus,
  updateSupportTicket,
} from "../../apis/supportService";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { adminService } from "../../apis/adminService";
import {
  RefreshCw,
  Loader2,
  Plus,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Paperclip,
  Maximize2,
} from "lucide-react";

import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { TicketAttachmentImage } from "../support/TicketAttachmentImage";

const STATUS_OPTIONS = ["OPEN", "PENDING", "RESOLVED", "CLOSED"];
const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"];

// Support ticket category topics used across the app
const CATEGORY_OPTIONS = [
  { value: "product-support", label: "Product support" },
  { value: "billing", label: "Billing & accounts" },
  { value: "integrations", label: "Integrations" },
  { value: "security", label: "Security & compliance" },
  { value: "feature-request", label: "Feature request" },
];

// Sorting options
const SORT_OPTIONS = [
  {
    value: "updatedAt_DESC",
    label: "Last Updated (Newest)",
    field: "updatedAt",
    order: "DESC",
  },
  {
    value: "updatedAt_ASC",
    label: "Last Updated (Oldest)",
    field: "updatedAt",
    order: "ASC",
  },
  {
    value: "createdAt_DESC",
    label: "Date Created (Newest)",
    field: "createdAt",
    order: "DESC",
  },
  {
    value: "createdAt_ASC",
    label: "Date Created (Oldest)",
    field: "createdAt",
    order: "ASC",
  },
  {
    value: "priority_DESC",
    label: "Priority (High to Low)",
    field: "priority",
    order: "DESC",
  },
  {
    value: "priority_ASC",
    label: "Priority (Low to High)",
    field: "priority",
    order: "ASC",
  },
  { value: "status_ASC", label: "Status (A-Z)", field: "status", order: "ASC" },
  {
    value: "status_DESC",
    label: "Status (Z-A)",
    field: "status",
    order: "DESC",
  },
  {
    value: "category_ASC",
    label: "Category (A-Z)",
    field: "category",
    order: "ASC",
  },
  {
    value: "category_DESC",
    label: "Category (Z-A)",
    field: "category",
    order: "DESC",
  },
  { value: "title_ASC", label: "Title (A-Z)", field: "title", order: "ASC" },
  { value: "title_DESC", label: "Title (Z-A)", field: "title", order: "DESC" },
];

// Priority order for proper sorting
const PRIORITY_ORDER = {
  CRITICAL: 5,
  URGENT: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

const statusStyles = {
  OPEN: "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200/80",
  PENDING: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200/80",
  RESOLVED: "bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200/80",
  CLOSED: "bg-slate-200 text-slate-700 border-slate-300 hover:bg-slate-300/80",
};

const priorityStyles = {
  LOW: "text-gray-600",
  MEDIUM: "text-blue-600",
  HIGH: "text-amber-600",
  URGENT: "text-red-600 font-semibold",
  CRITICAL: "text-red-700 font-bold",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

// Build a display Ticket ID: MMDDYYYY + first 4 chars of original id
const formatDisplayTicketId = (createdAt, originalId) => {
  try {
    const d = createdAt ? new Date(createdAt) : null;
    const mm = d ? String(d.getMonth() + 1).padStart(2, "0") : "";
    const dd = d ? String(d.getDate()).padStart(2, "0") : "";
    const yyyy = d ? String(d.getFullYear()) : "";
    const raw = String(originalId || "");
    const first4 = raw
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 4)
      .toUpperCase();
    const datePart = `${mm}${dd}${yyyy}`;
    if (!datePart && !first4) return "-";
    return `${datePart}${first4}`;
  } catch (_) {
    return "-";
  }
};

const defaultFilters = {
  status: "ALL",
  priority: "ALL",
  search: "",
  mineOnly: false,
  raisedByMe: false,
};

// Storage key for persisting settings
const STORAGE_KEY = "supportTicketsAdmin_settings";

// Function to load saved settings from localStorage
const loadSavedSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Failed to load saved settings:", error);
  }
  return null;
};

// Function to save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("Failed to save settings:", error);
  }
};

const SupportTicketsAdmin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [queryApplied, setQueryApplied] = useState(false);

  // Initialize state with saved settings or defaults
  const [filters, setFilters] = useState(() => {
    const saved = loadSavedSettings();
    return saved?.filters || defaultFilters;
  });

  const [searchInput, setSearchInput] = useState(() => {
    const saved = loadSavedSettings();
    return saved?.searchInput || "";
  });

  const [page, setPage] = useState(() => {
    const saved = loadSavedSettings();
    return saved?.page || 1;
  });

  const [limit, setLimit] = useState(() => {
    const saved = loadSavedSettings();
    return saved?.limit || 25;
  });

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pages: 1,
    total: 0,
    limit: 25,
  });

  const [teamMembers, setTeamMembers] = useState([]);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [updatingAssigneeId, setUpdatingAssigneeId] = useState(null);
  const { currentUser } = useAuth();
  const [requesterModal, setRequesterModal] = useState(null);
  // Consolidated Outlook-style inspector modal (replaces previous contentModal + attachmentsModal)
  const [detailsModal, setDetailsModal] = useState(null);
  const [attachmentIndex, setAttachmentIndex] = useState(0);
  // Full-screen attachment viewer state (opened by clicking the "Full Screen" button)
  const [fullscreenAttachment, setFullscreenAttachment] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
  // Searchable assignee dropdown state
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assigneeMenu, setAssigneeMenu] = useState({
    openFor: null,
    query: "",
    coords: null,
  });

  const [sort, setSort] = useState(() => {
    const saved = loadSavedSettings();
    return saved?.sort || "updatedAt_DESC";
  });

  // Ensure edit modal category select shows current value plus known options
  const categoryOptions = useMemo(() => {
    const base = [...CATEGORY_OPTIONS];
    const current = editModal?.category;
    if (current && !base.some((o) => String(o.value) === String(current))) {
      base.unshift({ value: current, label: current });
    }
    return base;
  }, [editModal?.category]);

  // Save settings whenever they change
  useEffect(() => {
    const settings = {
      filters,
      searchInput,
      page,
      limit,
      sort,
    };
    saveSettings(settings);
  }, [filters, searchInput, page, limit, sort]);

  const effectiveTeam = useMemo(() => {
    const mapped = Array.isArray(teamMembers) ? teamMembers : [];
    if (!currentUser) return mapped;
    const exists = mapped.some(
      (member) => member.userId === currentUser.userId,
    );
    if (exists) return mapped;
    return [
      ...mapped,
      {
        userId: currentUser.userId,
        label:
          [currentUser.profile?.firstName, currentUser.profile?.lastName]
            .filter(Boolean)
            .join(" ") || currentUser.email,
        email: currentUser.email,
      },
    ];
  }, [teamMembers, currentUser]);

  const loadTeamMembers = useCallback(async () => {
    try {
      const response = await adminService.getuser({ limit: 1000, role: "admin" });
      if (!response?.success) {
        return;
      }
      const raw = response.data;
      const candidates = Array.isArray(raw?.users)
        ? raw.users
        : Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

      const admins = candidates.filter((user) => {
        const roleValue =
          user?.role?.role || user?.role || (user?.roleid === 2 ? "admin" : "");
        return String(roleValue).toLowerCase() === "admin";
      });

      const formatted = admins.map((user) => ({
        userId: user.userId || user.userid || user.id,
        label:
          [user.profile?.firstName, user.profile?.lastName]
            .filter(Boolean)
            .join(" ") || user.email,
        email: user.email,
      }));

      if (formatted.length) {
        setTeamMembers(formatted);
      }
    } catch (error) {
      console.warn("Unable to load admin team", error);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      // Parse sort selection
      const [sortBy, sortOrder] = sort.split("_");

      const params = {
        limit,
        page,
        sortBy: sortBy || "updatedAt",
        sortOrder: sortOrder || "DESC",
      };
      if (filters.status && filters.status !== "ALL") {
        params.status = filters.status;
      }
      if (filters.priority && filters.priority !== "ALL") {
        params.priority = filters.priority;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.mineOnly && currentUser?.userId) {
        params.assignedTo = currentUser.userId;
      }
      if (filters.raisedByMe && currentUser?.userId) {
        params.userId = currentUser.userId;
      }

      const [ticketResponse, metricsResponse] = await Promise.all([
        listSupportTickets(params),
        getSupportTicketMetrics(),
      ]);

      setTickets(ticketResponse?.items || []);
      if (ticketResponse?.pagination) setPageInfo(ticketResponse.pagination);
      setMetrics(metricsResponse || null);
    } catch (error) {
      console.error("Failed to load tickets", error);
      toast.error("Unable to load support tickets right now");
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit, currentUser, sort]);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // If URL has ?ticketid=, apply it to search and then remove it after first load
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const ticketIdParam = params.get("ticketid");
      if (ticketIdParam && !queryApplied) {
        setPage(1);
        setSearchInput(ticketIdParam);
        setFilters((prev) => ({ ...prev, search: ticketIdParam }));
        setQueryApplied(true);
      }
    } catch (_) { }
  }, [location.search, queryApplied]);

  // After load completes once with query applied, strip ticketid param from URL
  useEffect(() => {
    if (!queryApplied || loading) return;
    const params = new URLSearchParams(location.search || "");
    if (params.has("ticketid")) {
      params.delete("ticketid");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }
  }, [queryApplied, loading, location.pathname, location.search, navigate]);

  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
  };

  const handleResetFilters = () => {
    setPage(1);
    setFilters(defaultFilters);
    setSearchInput("");
    setSort("updatedAt_DESC");
    // Also clear saved settings when resetting
    localStorage.removeItem(STORAGE_KEY);
  };

  const openEdit = (ticket, nextStatus = null) => {
    setEditModal({
      ticket,
      title: ticket.title || "",
      description: ticket.description || "",
      category: ticket.category || "",
      priority: ticket.priority || "MEDIUM",
      removeIds: new Set(),
      files: [],
      nextStatus,
    });
  };

  // Open the consolidated Outlook-style details inspector (matches user view)
  const openDetailsModal = (ticket) => {
    setDetailsModal(ticket);
    setAttachmentIndex(0);
    setFullscreenAttachment(false);
  };

  const submitEdit = async () => {
    if (!editModal?.ticket) return;
    const {
      ticket,
      title,
      description,
      category,
      priority,
      removeIds,
      files,
      nextStatus,
    } = editModal;
    setIsSaving(true);
    try {
      const form = new FormData();
      form.append("title", title || "");
      form.append("description", description || "");
      form.append("category", category || "");
      form.append("priority", priority || "MEDIUM");
      Array.from(removeIds || []).forEach((id) =>
        form.append("removeAttachmentIds", id),
      );
      (files || []).forEach((f) => form.append("attachments", f));
      await updateSupportTicket(ticket.ticketId, form);
      if (nextStatus && nextStatus !== ticket.status) {
        await changeStatus(ticket.ticketId, nextStatus);
      }
      toast.success("Ticket updated");
      setEditModal(null);
      await loadTickets();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update ticket");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSortChange = (newSort) => {
    setPage(1);
    setSort(newSort);
  };

  // Close assignee menu on Escape and click outside
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setAssigneeMenu({ openFor: null, query: "", coords: null });
    };
    const handleClickOutside = (e) => {
      if (assigneeMenu.openFor) {
        const isDropdownClick = e.target.closest(".assignee-dropdown-wrapper");
        if (!isDropdownClick) {
          setAssigneeMenu({ openFor: null, query: "", coords: null });
        }
      }
    };
    const handleScroll = (e) => {
      if (assigneeMenu.openFor && e.target) {
        if (e.target.classList && e.target.classList.contains("overflow-x-auto")) {
          setAssigneeMenu({ openFor: null, query: "", coords: null });
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [assigneeMenu.openFor]);

  // Full-screen attachment viewer: Escape to close + Left/Right arrows to navigate
  useEffect(() => {
    if (!fullscreenAttachment) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setFullscreenAttachment(false);
      } else if (e.key === "ArrowLeft") {
        setAttachmentIndex((index) => {
          const total = detailsModal?.attachments?.length || 1;
          return (index - 1 + total) % total;
        });
      } else if (e.key === "ArrowRight") {
        setAttachmentIndex((index) => {
          const total = detailsModal?.attachments?.length || 1;
          return (index + 1) % total;
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    // Prevent body scroll while fullscreen viewer is open
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreenAttachment, detailsModal]);

  const getAssigneeLabel = useCallback(
    (userId) => {
      if (!userId) return "Unassigned";
      const m = effectiveTeam.find((u) => String(u.userId) === String(userId));
      return m?.label || "Unassigned";
    },
    [effectiveTeam],
  );

  const getRequesterDisplayName = useCallback((ticket) => {
    if (!ticket) return "-";
    const profile = ticket.requester?.profile;
    const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim();
    if (fullName) return fullName;
    const directName = (ticket.requesterName || ticket.requester?.name || "").trim();
    if (directName) return directName;
    return ticket.requester?.email || "-";
  }, []);

  const filteredTeam = useMemo(() => {
    const q = (assigneeMenu.query || "").toLowerCase().trim();
    if (!q) return effectiveTeam;
    return effectiveTeam.filter(
      (m) =>
        String(m.label || "")
          .toLowerCase()
          .includes(q) ||
        String(m.email || "")
          .toLowerCase()
          .includes(q) ||
        String(m.userId || "")
          .toLowerCase()
          .includes(q),
    );
  }, [assigneeMenu.query, effectiveTeam]);

  const activeTicket = useMemo(() => {
    if (!assigneeMenu.openFor) return null;
    return tickets.find((t) => t.ticketId === assigneeMenu.openFor) || null;
  }, [tickets, assigneeMenu.openFor]);

  // Sortable header component for all columns
  const SortableHeader = ({
    label,
    sortField,
    currentSort,
    className = "",
  }) => {
    const [currentSortBy, currentSortOrder] = currentSort.split("_");
    const isActive = currentSortBy === sortField;
    const isAsc = currentSortOrder === "ASC";

    return (
      <th
        className={`px-1 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer hover:bg-gray-100 ${className}`}
        onClick={() => {
          const newSort =
            isActive && isAsc ? `${sortField}_DESC` : `${sortField}_ASC`;
          handleSortChange(newSort);
        }}
      >
        <div className="flex items-center gap-1">
          {label}
          {/* ALWAYS show both chevrons */}
          <div className="flex flex-col -space-y-1">
            <ChevronUp
              className={`h-2 w-2 ${isActive && isAsc ? "text-gray-700" : "text-gray-400"
                }`}
            />
            <ChevronDown
              className={`h-2 w-2 ${isActive && !isAsc ? "text-gray-700" : "text-gray-400"
                }`}
            />
          </div>
        </div>
      </th>
    );
  };


  const assignToUsers = async (ticketId, userIds) => {
    setUpdatingAssigneeId(ticketId);
    try {
      // Call the updated API
      await assignSupportTicket(ticketId, userIds);
      toast.success("Assignees updated");
      // Optimistically update the ticket in state
      setTickets((prev) =>
        prev.map((t) =>
          t.ticketId === ticketId
            ? {
              ...t,
              status: userIds.length > 0 && t.status === "OPEN" ? "PENDING" : t.status,
              assignees: userIds.map((id) => {
                const member = effectiveTeam.find(
                  (m) => String(m.userId) === String(id),
                );
                return { userId: id, email: member?.email || "" };
              }),
            }
            : t,
        ),
      );
      await loadTickets(); // or just rely on optimistic update if you prefer
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to assign");
    } finally {
      setUpdatingAssigneeId(null);
    }
  };
  const changeStatus = async (ticketId, status) => {
    setUpdatingStatusId(ticketId);
    try {
      await updateSupportTicketStatus(ticketId, status);
      toast.success(`Status updated to ${status}`);
      await loadTickets();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 mx-auto w-full overflow-x-hidden">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Track Tickets</h2>
          <p className="text-sm text-gray-600">
            Monitor open issues and triage assignments in real time.
          </p>
        </div>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
          <Link
            to="/support-ticket"
            className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-teal-600 p-2 text-sm font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:w-auto"
          >
            <Plus className="h-4 w-4" />{" "}
            <span className="text-xs">New Ticket</span>
          </Link>
          <form
            onSubmit={handleSearchSubmit}
            className="flex items-center gap-2"
          >
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search Ticket ID, title, description"
              className="h-9 w-64 rounded-md border border-gray-200 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-gray-300"
            >
              Search
            </button>
          </form>

          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center gap-2 rounded-md border border-transparent px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={loadTickets}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(event) =>
              handleFilterChange("status", event.target.value)
            }
            className="h-8 rounded-md border border-gray-200 px-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="ALL">All</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Priority
          </label>
          <select
            value={filters.priority}
            onChange={(event) =>
              handleFilterChange("priority", event.target.value)
            }
            className="h-8 rounded-md border border-gray-200 px-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="ALL">All</option>
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        {/* Sort Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Sort By
          </label>
          <select
            value={sort}
            onChange={(event) => handleSortChange(event.target.value)}
            className="h-8 rounded-md border border-gray-200 px-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const all = [];
              let page = 1;
              const limit = 200;
              while (true) {
                const res = await listSupportTickets({
                  page,
                  limit,
                  sortBy: "updatedAt",
                  sortOrder: "DESC",
                });
                const items = res?.items || [];
                all.push(...items);
                const pg = res?.pagination;
                if (!pg || page >= (pg.pages || 1)) break;
                page++;
              }
              const rows = all.map((t) => ({
                title: t.title || "",
                description: t.description || "",
                category: t.category || "",
                status: t.status || "",
                priority: t.priority || "",
                assignee: t.assignee?.email || "",
                requester: t.requester?.email || "",
                updatedAt: t.updatedAt || "",
                createdAt: t.createdAt || "",
              }));
              const headers = Object.keys(
                rows[0] || {
                  title: "",
                  description: "",
                  category: "",
                  status: "",
                  priority: "",
                  assignee: "",
                  requester: "",
                  updatedAt: "",
                  createdAt: "",
                },
              );
              const csv = [
                headers.join(","),
                ...rows.map((r) =>
                  headers
                    .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
                    .join(","),
                ),
              ].join("\r\n");
              const blob = new Blob(["\uFEFF" + csv], {
                type: "text/csv;charset=utf-8;",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "support_tickets.csv";
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) {
              toast.error("CSV export failed");
            }
          }}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-300"
        >
          Download CSV
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!filters.mineOnly}
            onChange={(e) => {
              setPage(1);
              handleFilterChange("mineOnly", e.target.checked);
            }}
          />
          Tickets assigned to me
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!filters.raisedByMe}
            onChange={(e) => {
              setPage(1);
              handleFilterChange("raisedByMe", e.target.checked);
            }}
          />
          Tickets raised by me
        </label>
        {filters.search && (
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
            Search: {filters.search}
            <button
              type="button"
              className="text-blue-700"
              onClick={() => setFilters((prev) => ({ ...prev, search: "" }))}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {metrics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Open"
            value={metrics.totals?.openTickets ?? 0}
            tone="blue"
          />
          <MetricCard
            label="Pending"
            value={metrics.totals?.pendingTickets ?? 0}
            tone="amber"
          />
          <MetricCard
            label="Resolved"
            value={metrics.totals?.resolvedTickets ?? 0}
            tone="teal"
          />
          <MetricCard
            label="Resolution rate"
            value={`${metrics.averages?.resolutionRate ?? 0}%`}
            tone="blue"
            caption={`First response avg. ${metrics.averages?.firstResponseMinutes ?? "-"
              } min`}
          />
        </div>
      )}

      <div className="hidden md:block overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Top Pagination Bar */}
        {!loading && (
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 text-sm text-gray-600 bg-white">
            <div className="font-medium text-xs sm:text-sm">
              Page {pageInfo.page} of {pageInfo.pages || 1} • {pageInfo.total} total tickets
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Rows:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(Number(e.target.value) || 25);
                }}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageInfo.page <= 1}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((p) => Math.min(pageInfo.pages || 1, p + 1))
                }
                disabled={pageInfo.page >= (pageInfo.pages || 1)}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            <col className="w-28" />   {/* Ticket ID */}
            <col className="w-28" />   {/* Actions */}
            <col className="w-28" />   {/* Category */}
            <col className="w-44" />   {/* Title */}
            <col className="w-24" />   {/* Status */}
            <col className="w-24" />   {/* Priority */}
            <col className="w-24" />   {/* Details (formerly Attachments) */}
            <col className="w-32" />   {/* Assigned to */}
            <col className="w-36" />   {/* Created By */}
            <col className="w-40" />   {/* Created At */}
            <col className="w-40" />   {/* Updated At */}
          </colgroup>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ticket ID
              </th>
              <SortableHeader
                label="Actions"
                sortField="createdAt"
                currentSort={sort}
                className="px-1 py-3"
              />
              <SortableHeader
                label="Category"
                sortField="category"
                currentSort={sort}
                className="px-1 py-3"
              />
              <SortableHeader
                label="Title"
                sortField="title"
                currentSort={sort}
                className="px-3 py-3"
              />
              <SortableHeader
                label="Status"
                sortField="status"
                currentSort={sort}
                className="px-1 py-3"
              />
              <SortableHeader
                label="Priority"
                sortField="priority"
                currentSort={sort}
                className="px-1 py-3"
              />
              {/* Renamed from "Attachments" to "Details" */}
              <SortableHeader
                label="Details"
                sortField="createdAt"
                currentSort={sort}
                className="px-1 py-3"
              />
              <SortableHeader
                label="Assigned to"
                sortField="createdAt"
                currentSort={sort}
                className="px-1 py-3"
              />
              <SortableHeader
                label="Created By"
                sortField="createdAt"
                currentSort={sort}
                className="px-2 py-3"
              />
              <SortableHeader
                label="Created At"
                sortField="createdAt"
                currentSort={sort}
                className="px-3 py-3"
              />
              <SortableHeader
                label="Updated At"
                sortField="updatedAt"
                currentSort={sort}
                className="px-3 py-3"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {tickets.map((ticket) => (
              <tr key={ticket.ticketId} className="hover:bg-gray-50">
                <td className="px-2 py-3 text-sm font-mono text-gray-700">
                  {formatDisplayTicketId(ticket.createdAt, ticket.ticketId)}
                </td>
                <td className="px-1 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {ticket.status === "RESOLVED" ? (
                      <button
                        className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 disabled:opacity-60"
                        onClick={() => {
                          const isOwner =
                            ticket.requester?.userId &&
                            currentUser?.userId &&
                            String(ticket.requester.userId) ===
                            String(currentUser.userId);
                          if (isOwner) openEdit(ticket, "OPEN");
                          else changeStatus(ticket.ticketId, "OPEN");
                        }}
                        disabled={updatingStatusId === ticket.ticketId}
                      >
                        {updatingStatusId === ticket.ticketId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : null}
                        Reopen
                      </button>
                    ) : null}
                    {ticket.status !== "RESOLVED" &&
                      ticket.status !== "CLOSED" ? (
                      <button
                        className="inline-flex items-center gap-1 rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-teal-200 disabled:opacity-60"
                        onClick={() =>
                          changeStatus(ticket.ticketId, "RESOLVED")
                        }
                        disabled={updatingStatusId === ticket.ticketId}
                      >
                        {updatingStatusId === ticket.ticketId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : null}
                        Resolve
                      </button>
                    ) : null}

                    <button
                      className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-200"
                      onClick={() => setDeleteConfirmModal(ticket)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-xs text-gray-600 uppercase">
                    {ticket.category}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-sm text-gray-900">
                    {ticket.title || (ticket.description || "").slice(0, 60)}
                  </div>
                </td>

                <td className="px-1 py-3">
                  {ticket.status === "CLOSED" ? (
                    <span
                      className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyles[ticket.status] || ""
                        }`}
                    >
                      {ticket.status}
                    </span>
                  ) : (
                    <div className="relative inline-block">
                      <select
                        value={ticket.status}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === "OPEN" && ticket.status !== "OPEN") {
                            const isOwner =
                              ticket.requester?.userId &&
                              currentUser?.userId &&
                              String(ticket.requester.userId) ===
                                String(currentUser.userId);
                            if (isOwner) {
                              openEdit(ticket, "OPEN");
                              return;
                            }
                          }
                          changeStatus(ticket.ticketId, value);
                        }}
                        disabled={updatingStatusId === ticket.ticketId}
                        className={`inline-flex items-center rounded-full border px-2.5 py-1 pr-6 text-xs font-semibold cursor-pointer appearance-none transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-60 ${statusStyles[ticket.status] || "bg-gray-100 text-gray-700 border-gray-200"
                          }`}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option
                            key={option}
                            value={option}
                            className="bg-white text-gray-900 font-medium"
                          >
                            {option}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 opacity-60 text-current" />
                    </div>
                  )}
                </td>
                <td
                  className={`px-1 py-3 text-sm ${priorityStyles[ticket.priority] || ""
                    }`}
                >
                  {ticket.priority}
                </td>
                {/* Details column: single "View details" button opening consolidated Outlook-style modal */}
                <td className="px-1 py-3 text-sm text-gray-600">
                  <button
                    type="button"
                    className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                    onClick={() => openDetailsModal(ticket)}
                  >
                    View details
                  </button>
                </td>
                <td className="px-1 py-3 text-sm text-gray-600">
                  <div className="relative inline-block assignee-dropdown-wrapper">
                    <button
                      type="button"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        const spaceAbove = rect.top;
                        const openUpwards = spaceBelow < 360 && spaceAbove > spaceBelow;

                        setSelectedAssignees(ticket.assignees || []);
                        setAssigneeMenu((m) => ({
                          openFor:
                            m.openFor === ticket.ticketId
                              ? null
                              : ticket.ticketId,
                          query: "",
                          coords: {
                            top: openUpwards
                              ? rect.top + window.scrollY
                              : rect.bottom + window.scrollY,
                            left: Math.max(10, rect.right - 320 + window.scrollX),
                            openUpwards,
                          },
                        }));
                      }}
                      disabled={updatingAssigneeId === ticket.ticketId}
                      className="inline-flex min-w-[10rem] items-center justify-between rounded-md border border-gray-200 bg-white px-2 py-1 text-sm hover:bg-gray-50 disabled:opacity-60"
                    >
                      <span>
                        {ticket.assignees?.length
                          ? `${getAssigneeLabel(ticket.assignees[0].userId)}${ticket.assignees.length > 1 ? ` +${ticket.assignees.length - 1}` : ""}`
                          : "Unassigned"}
                      </span>
                      <ChevronDown className="ml-2 h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </div>
                </td>
                <td className="px-2 py-3 text-sm text-gray-500 whitespace-nowrap">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                    onClick={() => setRequesterModal(ticket)}
                  >
                    {getRequesterDisplayName(ticket)}
                  </button>
                </td>
                <td className="px-2 py-3 text-sm text-gray-500 whitespace-nowrap">
                  {formatDateTime(ticket.createdAt)}
                </td>
                <td className="px-2 py-3 text-sm text-gray-500 whitespace-nowrap">
                  {formatDateTime(ticket.updatedAt)}
                </td>
              </tr>
            ))}
            {(!tickets || tickets.length === 0) && !loading && (
              <tr>
                <td
                  colSpan={11}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  No tickets found yet. New submissions will appear here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading support tickets...
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3">
        {/* Mobile Sort Control */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 block">
            Sort By
          </label>
          <select
            value={sort}
            onChange={(event) => handleSortChange(event.target.value)}
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Mobile Top Pagination */}
        {!loading && (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs text-gray-600 shadow-sm">
            <div>
              Page {pageInfo.page} of {pageInfo.pages || 1} • {pageInfo.total} total
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageInfo.page <= 1}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((p) => Math.min(pageInfo.pages || 1, p + 1))
                }
                disabled={pageInfo.page >= (pageInfo.pages || 1)}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
        {tickets.map((ticket) => (
          <div
            key={ticket.ticketId}
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyles[ticket.status] || ""
                  }`}
              >
                {ticket.status}
              </span>
              <button
                type="button"
                className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                onClick={() => setRequesterModal(ticket)}
              >
                {getRequesterDisplayName(ticket)}
              </button>
            </div>
            <div className="mt-1 flex flex-col gap-0.5 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <div>
                  ID:{" "}
                  <span className="font-mono">
                    {formatDisplayTicketId(ticket.createdAt, ticket.ticketId)}
                  </span>
                </div>
                <div><span className="text-gray-400">Created:</span> {formatDateTime(ticket.createdAt)}</div>
              </div>
              <div className="text-right">
                <span className="text-gray-400">Updated:</span> {formatDateTime(ticket.updatedAt)}
              </div>
            </div>
            <div className="mt-1 text-xs text-gray-500">{ticket.title}</div>
            <div className="mt-1 text-xs uppercase text-gray-500">
              {ticket.category}
            </div>
            {/* Consolidated details button (mirrors user view) */}
            <button
              type="button"
              className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
              onClick={() => openDetailsModal(ticket)}
            >
              View details
            </button>
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="relative w-1/2 assignee-dropdown-wrapper">
                <button
                  type="button"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    const openUpwards = spaceBelow < 360 && spaceAbove > spaceBelow;

                    setSelectedAssignees(ticket.assignees || []);
                    setAssigneeMenu((m) => ({
                      openFor:
                        m.openFor === ticket.ticketId ? null : ticket.ticketId,
                      query: "",
                      coords: {
                        top: openUpwards
                          ? rect.top + window.scrollY
                          : rect.bottom + window.scrollY,
                        left: Math.max(10, rect.right - 320 + window.scrollX),
                        openUpwards,
                      },
                    }));
                  }}
                  disabled={updatingAssigneeId === ticket.ticketId}
                  className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-60"
                >
                  <span className="truncate pr-2">
                    {ticket.assignees?.length
                      ? `${getAssigneeLabel(ticket.assignees[0].userId)}${ticket.assignees.length > 1 ? ` +${ticket.assignees.length - 1}` : ""}`
                      : "Unassigned"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {ticket.status === "RESOLVED" ? (
                  <button
                    className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700"
                    onClick={() => {
                      const isOwner =
                        ticket.requester?.userId &&
                        currentUser?.userId &&
                        String(ticket.requester.userId) ===
                        String(currentUser.userId);
                      if (isOwner) openEdit(ticket, "OPEN");
                      else changeStatus(ticket.ticketId, "OPEN");
                    }}
                    disabled={updatingStatusId === ticket.ticketId}
                  >
                    Reopen
                  </button>
                ) : null}
                {ticket.status !== "RESOLVED" && ticket.status !== "CLOSED" ? (
                  <button
                    className="rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-700"
                    onClick={() => changeStatus(ticket.ticketId, "RESOLVED")}
                    disabled={updatingStatusId === ticket.ticketId}
                  >
                    Resolve
                  </button>
                ) : null}

                <button
                  className="rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700"
                  onClick={() => setDeleteConfirmModal(ticket)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {(!tickets || tickets.length === 0) && !loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
            No tickets found yet.
          </div>
        )}
      </div>
      {/* Requester details modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setEditModal(null)}
          />
          <div className="relative z-10 w-[95%] max-w-3xl rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Edit Ticket
              </h3>
              <button
                className="text-gray-500"
                onClick={() => setEditModal(null)}
              >
                ✕
              </button>
            </div>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span className="font-mono rounded bg-gray-100 px-1.5 py-0.5">
                #
                {formatDisplayTicketId(
                  editModal.ticket.createdAt,
                  editModal.ticket.ticketId,
                )}
              </span>
              <span
                className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${statusStyles[editModal.ticket.status] || "bg-slate-100 text-slate-700"}`}
              >
                {editModal.ticket.status}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600">Title</span>
                <input
                  value={editModal.title}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, title: e.target.value }))
                  }
                  placeholder="Brief summary of the issue"
                  className="rounded-md border border-gray-200 px-2 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600">Category</span>
                <select
                  value={editModal.category}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, category: e.target.value }))
                  }
                  className="rounded-md border border-gray-200 px-2 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600">Priority</span>
                <select
                  value={editModal.priority}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, priority: e.target.value }))
                  }
                  className="rounded-md border border-gray-200 px-2 py-2 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-600">Status</span>
                <select
                  value={editModal.nextStatus ?? editModal.ticket.status}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, nextStatus: e.target.value }))
                  }
                  className="rounded-md border border-gray-200 px-2 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs text-gray-600">Description</span>
                <textarea
                  value={editModal.description}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, description: e.target.value }))
                  }
                  rows={5}
                  placeholder="Include details, error messages, steps to reproduce, and expected behavior"
                  className="rounded-md border border-gray-200 px-2 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <div className="mt-1 text-xs text-gray-500">
                  {(editModal.description || "").length} characters
                </div>
              </label>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">
                    Current Attachments
                  </div>
                  <div className="max-h-44 overflow-auto rounded-md border border-gray-100 p-2">
                    {(editModal.ticket.attachments || []).length === 0 && (
                      <div className="text-xs text-gray-500">
                        No attachments
                      </div>
                    )}
                    {(editModal.ticket.attachments || []).map((a) => (
                      <label
                        key={a.attachmentId}
                        className="flex items-center gap-2 text-xs text-gray-700 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={editModal.removeIds.has(a.attachmentId)}
                          onChange={(e) => {
                            setEditModal((m) => {
                              const next = new Set(m.removeIds);
                              if (e.target.checked) next.add(a.attachmentId);
                              else next.delete(a.attachmentId);
                              return { ...m, removeIds: next };
                            });
                          }}
                        />
                        <span className="truncate">
                          {a.originalName || a.filename}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">
                    Add Attachments
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={(e) =>
                      setEditModal((m) => ({
                        ...m,
                        files: Array.from(e.target.files || []),
                      }))
                    }
                    className="text-xs"
                  />
                  {(editModal.files || []).length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Selected:{" "}
                      {(editModal.files || [])
                        .map((f) => f.name)
                        .slice(0, 3)
                        .join(", ")}
                      {(editModal.files || []).length > 3 ? "…" : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                onClick={() => setEditModal(null)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                onClick={submitEdit}
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : ""}
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
      {requesterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setRequesterModal(null)}
          />
          <div className="relative z-10 w-[90%] max-w-md rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Ticket Raised by
              </h3>
              <button
                className="text-gray-500"
                onClick={() => setRequesterModal(null)}
              >
                ✕
              </button>
            </div>
            <div className="space-y-2 text-sm text-gray-700">
              <div>
                <span className="font-semibold">Name:</span>{" "}
                {[
                  requesterModal.requester?.profile?.firstName,
                  requesterModal.requester?.profile?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ") || "-"}
              </div>
              <div>
                <span className="font-semibold">Email:</span>{" "}
                {requesterModal.requester?.email || "-"}
              </div>
              <div>
                <span className="font-semibold">Role:</span>{" "}
                {requesterModal.requester?.profile?.roleTitle || "-"}
              </div>
              <div>
                <span className="font-semibold">Organization:</span>{" "}
                {requesterModal.requester?.profile?.organization ||
                  requesterModal.requester?.profile?.practiceName ||
                  "-"}
              </div>
              <div>
                <span className="font-semibold">Category:</span>{" "}
                {requesterModal.category}
              </div>
              <div>
                <span className="font-semibold">Priority:</span>{" "}
                {requesterModal.priority}
              </div>
              <div>
                <span className="font-semibold">Status:</span>{" "}
                {requesterModal.status}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outlook-style ticket inspector (matches user view) */}
      {detailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetailsModal(null)} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-base font-semibold text-gray-900">
                Ticket #{formatDisplayTicketId(detailsModal.createdAt, detailsModal.ticketId)}
              </h3>
              <button
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                onClick={() => setDetailsModal(null)}
                aria-label="Close details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
            <div className={`${Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? "grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" : ""}`}>
              <div className={`p-5 text-left text-sm ${Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? "space-y-4 border-b border-gray-200 md:border-b-0 md:border-r" : "flex flex-wrap items-start gap-x-10 gap-y-4 [&>div]:min-w-0 [&>div]:max-w-full [&>div]:break-words"}`}>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Subject</div>
                <div className="mt-1 font-medium text-gray-900">{detailsModal.title || '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Category</div>
                <div className="mt-1 font-medium text-gray-900">{detailsModal.category || '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Priority</div>
                <div className={`mt-1 font-medium ${priorityStyles[detailsModal.priority] || 'text-gray-900'}`}>
                  {detailsModal.priority || '-'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Status</div>
                <span className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyles[detailsModal.status] || 'bg-slate-100 text-slate-700'}`}>
                  {detailsModal.status}
                </span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Created By</div>
                <div className="mt-1 font-medium text-gray-900">{getRequesterDisplayName(detailsModal)}</div>
              </div>
              </div>
              {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 && (
              <div className="min-w-0 bg-gray-50 p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({Array.isArray(detailsModal.attachments) ? detailsModal.attachments.length : 0})
                  </div>
                  <div className="flex items-center gap-3">
                    {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 && (
                      <>
                        <span className="text-xs text-gray-500">{attachmentIndex + 1} of {detailsModal.attachments.length}</span>
                        {/* Full screen toggle button beside Attachments label */}
                        <button
                          type="button"
                          onClick={() => setFullscreenAttachment(true)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50"
                          aria-label="View attachment in full screen"
                          title="View in full screen"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                          Full Screen
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? (
                  <div className="relative aspect-video overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <TicketAttachmentImage
                      key={detailsModal.attachments[attachmentIndex]?.attachmentId || attachmentIndex}
                      ticketId={detailsModal.ticketId}
                      attachment={detailsModal.attachments[attachmentIndex]}
                      alt={detailsModal.attachments[attachmentIndex]?.originalName || "attachment"}
                      className="h-full w-full object-contain"
                    />
                    {detailsModal.attachments.length > 1 && (
                      <>
                        <button type="button" aria-label="Previous attachment" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow hover:bg-white" onClick={() => setAttachmentIndex((index) => (index - 1 + detailsModal.attachments.length) % detailsModal.attachments.length)}>
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Next attachment" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow hover:bg-white" onClick={() => setAttachmentIndex((index) => (index + 1) % detailsModal.attachments.length)}>
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
                {detailsModal.attachments[attachmentIndex]?.originalName && (
                  <div className="mt-2 truncate text-xs text-gray-500" title={detailsModal.attachments[attachmentIndex].originalName}>
                    {detailsModal.attachments[attachmentIndex].originalName}
                  </div>
                )}
              </div>
              )}
            </div>
            {/* Message shown as a full-width paragraph below the details/attachments */}
            <div className="border-t border-gray-200 p-5 text-left text-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">Message</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-gray-700">{detailsModal.description || '-'}</div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen attachment viewer (opened via the "Full Screen" button) */}
      {fullscreenAttachment &&
        detailsModal &&
        Array.isArray(detailsModal.attachments) &&
        detailsModal.attachments.length > 0 && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95"
            onClick={() => setFullscreenAttachment(false)}
          >
            {/* Top bar with filename + close */}
            <div
              className="absolute left-0 right-0 top-0 flex items-center justify-between gap-3 bg-gradient-to-b from-black/80 to-transparent px-4 py-3 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {detailsModal.attachments[attachmentIndex]?.originalName || "Attachment"}
                </div>
                <div className="text-xs text-white/70">
                  {attachmentIndex + 1} of {detailsModal.attachments.length} • Ticket #
                  {formatDisplayTicketId(detailsModal.createdAt, detailsModal.ticketId)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFullscreenAttachment(false)}
                aria-label="Close full screen"
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Image (clicking it does not close the viewer) */}
            <TicketAttachmentImage
              key={detailsModal.attachments[attachmentIndex]?.attachmentId || attachmentIndex}
              ticketId={detailsModal.ticketId}
              attachment={detailsModal.attachments[attachmentIndex]}
              alt={detailsModal.attachments[attachmentIndex]?.originalName || "attachment"}
              className="max-h-[85vh] max-w-[92vw] object-contain select-none"
              fallbackClassName="!h-auto !w-auto px-6 py-4 text-white/80"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Prev / Next navigation */}
            {detailsModal.attachments.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous attachment"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentIndex((index) =>
                      (index - 1 + detailsModal.attachments.length) %
                      detailsModal.attachments.length,
                    );
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white shadow hover:bg-white/25"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  aria-label="Next attachment"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentIndex(
                      (index) => (index + 1) % detailsModal.attachments.length,
                    );
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white shadow hover:bg-white/25"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Bottom thumbnail strip for quick jumps */}
            {detailsModal.attachments.length > 1 && (
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/80 to-transparent px-4 py-3"
                onClick={(e) => e.stopPropagation()}
              >
                {detailsModal.attachments.map((att, idx) => (
                  <button
                    key={att.attachmentId || idx}
                    type="button"
                    onClick={() => setAttachmentIndex(idx)}
                    className={`h-12 w-16 overflow-hidden rounded border-2 transition ${
                      idx === attachmentIndex
                        ? "border-white"
                        : "border-white/30 hover:border-white/60"
                    }`}
                    aria-label={`View attachment ${idx + 1}`}
                  >
                    <TicketAttachmentImage
                      ticketId={detailsModal.ticketId}
                      attachment={att}
                      alt={att.originalName || `attachment ${idx + 1}`}
                      className="h-full w-full object-cover"
                      fallbackClassName="text-white/80"
                      compact
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      {activeTicket && assigneeMenu.coords && createPortal(
        <div
          className="absolute z-[100] w-80 rounded-md border border-gray-200 bg-white p-2 shadow-lg assignee-dropdown-wrapper"
          style={{
            top: `${assigneeMenu.coords.top}px`,
            left: `${assigneeMenu.coords.left}px`,
            transform: assigneeMenu.coords.openUpwards ? "translateY(-100%) translateY(-4px)" : "none",
          }}
        >
          <input
            value={assigneeMenu.query}
            onChange={(e) =>
              setAssigneeMenu((m) => ({
                ...m,
                query: e.target.value,
              }))
            }
            placeholder="Search members..."
            className="h-8 w-full rounded-md border border-gray-200 px-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <div className="mt-2 max-h-64 overflow-auto">
            {filteredTeam.map((member) => (
              <label
                key={member.userId}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedAssignees.some(
                    (a) => a.userId === member.userId,
                  )}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAssignees((prev) => [
                        ...prev,
                        member,
                      ]);
                    } else {
                      setSelectedAssignees((prev) =>
                        prev.filter(
                          (a) => a.userId !== member.userId,
                        ),
                      );
                    }
                  }}
                />
                <span className="text-sm">
                  {member.label}
                  {member.email && <span className="text-xs text-gray-500 ml-1">({member.email})</span>}
                </span>
              </label>
            ))}
            {filteredTeam.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500">
                No matches
              </div>
            )}
          </div>
          <div className="mt-2 flex justify-end gap-2 border-t pt-2">
            <button
              className="px-2 py-1 text-xs rounded-md border"
              onClick={() => {
                setAssigneeMenu({ openFor: null, query: "", coords: null });
                setSelectedAssignees([]);
              }}
            >
              Cancel
            </button>
            <button
              className="px-2 py-1 text-xs rounded-md bg-blue-600 text-white"
              onClick={() => {
                assignToUsers(
                  activeTicket.ticketId,
                  selectedAssignees.map((a) => a.userId),
                );
                setAssigneeMenu({ openFor: null, query: "", coords: null });
                setSelectedAssignees([]);
              }}
            >
              Save
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirmModal(null)} />
          <div className="relative z-10 w-[90%] max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600 mb-3">
                <Trash2 className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Delete Ticket</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this ticket permanently? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmModal(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ticket = deleteConfirmModal;
                  setDeleteConfirmModal(null);
                  try {
                    const { deleteSupportTicket } = await import("../../apis/supportService");
                    await deleteSupportTicket(ticket.ticketId);
                    toast.success("Ticket deleted");
                    await loadTickets();
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.error || "Failed to delete ticket"
                    );
                  }
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const MetricCard = ({ label, value, caption }) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {caption && <div className="mt-1 text-xs text-gray-600">{caption}</div>}
    </div>
  );
};

export default SupportTicketsAdmin;